import React, { useState, useEffect, Fragment } from 'react'
import { INVALID_PRODUCT_TYPES } from '@constants/productConstants'
import { convertToCurrency } from '@utils/utils.js'


const PriceNosto = ({
    product,
    brand,
    priceType,
    matchingVariants,
    variantIdOnLoad,
    isGiftCard,
    placement,
}) => {
    // Handle both field name variations for price data
    const productPrice = product?.price || product?.productPrice
    const productListPrice = product?.listPrice || product?.productListPrice



    let is10Off = false
    let is20Off = false
    let is25Off = false
    let is30Off = false
    let is50Off = false
    let is60Off = false
    let is70Off = false
    let isDoorbuster = false
    let is40OffDresses = false
    let isHeartsCollection = false
    let isUppababySale = false
    let isDisney = false



    // Handle both data structures - existing (PLP/PDP) and Nosto recommendation
    const tags = product?.tags1 || product?.productTags
    const vendor = product?.brand || product?.productBrand || brand || 'Monica + Andy'

    const productType =
        product?.productType ||
        product?.customFields?.find((field) => field.key === 'producttype')
            ?.value

    // Get product name from either structure
    const productName = product?.name || product?.productName



    const isInvalidProductType = INVALID_PRODUCT_TYPES.includes(productType)

    const variants = product?.skus

    let firstVariantWithInventory

    if (tags?.includes('uppababysale')) {
        isUppababySale = true
    }

    if (vendor === 'Monica + Andy' && !isInvalidProductType) {
        firstVariantWithInventory = variants?.find(
            (variant) => variant?.inventoryLevel > 0
        )

        // if (
        //     (tags && tags?.includes('fallhalloween2024')) ||
        //     tags?.includes('halloween')
        // ) {
        //     is50Off = true
        // }

        if (tags?.includes('doorbuster2024')) {
            isDoorbuster = true
        }

        if (tags?.includes('dressesvday40')) {
            is40OffDresses = true
        }

        if (tags?.includes('vday2024')) {
            isHeartsCollection = true
        }

        if (tags?.includes('disney')) {
            isDisney = true
        }

        // Also check if product name contains "Disney" (case insensitive)
        if (productName?.toLowerCase().includes('disney')) {
            isDisney = true
        }

        if (tags?.includes('pjswim30off')) {
            is30Off = true
        } else {
            is30Off = false
        }



    } else if (
        matchingVariants &&
        matchingVariants.length > 0 &&
        !isInvalidProductType
    ) {
        firstVariantWithInventory = matchingVariants
            ? matchingVariants[0]
            : variants[0]
    } else if (variantIdOnLoad) {
        firstVariantWithInventory = variants?.find(
            (variant) => variant?.id?.toString() === variantIdOnLoad?.toString()
        )
    } else if (!isInvalidProductType) {
        firstVariantWithInventory = variants[0]
    }

    const [price, setPrice] = useState({})
    const [discountPercent, setDiscountPercent] = useState(0)

    useEffect(() => {
        const roundIfNeeded = (price) => {
            const decimal = price % 1
            const firstDecimal = Math.floor((decimal * 10) % 10)
            const secondDecimal = Math.floor((decimal * 100) % 10)
            const thirdDecimal = Math.floor((decimal * 1000) % 10)

            return firstDecimal === 9 ||
                secondDecimal === 9 ||
                thirdDecimal >= 5
                ? Math.ceil(price)
                : price
        }

        const convertNostoPrice = (priceValue) => {
            if (!priceValue) return 0
            // If it's already a number, return it
            if (typeof priceValue === 'number') return priceValue
            // If it's a string, remove $ and convert to number
            return parseFloat(priceValue.replace('$', ''))
        }

        const convertToCurrencyNoRound = (price) => {
            if (!price) return 0
            return parseFloat(price)
        }

        // Get price and listPrice from either data structure
        const currentProductPrice = product?.price || product?.productPrice
        const currentListPrice = product?.listPrice || product?.productListPrice

        if (vendor === 'Monica + Andy' && !isInvalidProductType) {
            // Check if product is on SALE - gets an ADDITIONAL 50% off already marked down prices
            if (product?.name && product?.name.includes('SALE')) {
                // For SALE products, try to get pricing from first available SKU if product-level pricing is missing
                let priceToUse = currentProductPrice;
                let listPriceToUse = currentListPrice;

                if ((!priceToUse || !listPriceToUse) && firstVariantWithInventory) {
                    priceToUse = priceToUse || firstVariantWithInventory.price;
                    listPriceToUse = listPriceToUse || firstVariantWithInventory.listPrice;
                } else if ((!priceToUse || !listPriceToUse) && variants?.[0]) {
                    priceToUse = priceToUse || variants[0].price;
                    listPriceToUse = listPriceToUse || variants[0].listPrice;
                }

                const originalListPrice = convertToCurrency(convertNostoPrice(listPriceToUse) || listPriceToUse);
                const salePrice = convertNostoPrice(priceToUse) || priceToUse;

                // SALE products get an ADDITIONAL 50% off their already discounted price
                const extraDiscountPrice = convertToCurrencyNoRound(salePrice * 0.5 + 0.01);

                setPrice({
                    price: convertToCurrency(salePrice), // Show the current sale price
                    compareAtPrice: originalListPrice, // Show comparison against original list price
                    discountPrice: extraDiscountPrice, // Apply additional 50% discount
                });

                // Calculate total discount percent from original list price to final price
                const listPriceNum = parseFloat(originalListPrice) || 0;
                const finalPriceNum = parseFloat(extraDiscountPrice) || 0;

                if (listPriceNum > finalPriceNum && finalPriceNum > 0 && listPriceNum > 0) {
                    const calculatedDiscount = Math.round(((listPriceNum - finalPriceNum) / listPriceNum) * 100);
                    setDiscountPercent(calculatedDiscount);
                } else {
                    setDiscountPercent(0);
                }
                return;
            }

            // 50% off for Halloween products
            if (is50Off) {
                const regularPrice = convertNostoPrice(productPrice) || productPrice
                setPrice({
                    price: convertToCurrency(regularPrice),
                    compareAtPrice: convertToCurrency(productListPrice || productPrice),
                    discountPrice: convertToCurrencyNoRound(regularPrice * 0.5),
                });
                setDiscountPercent(50);
                return;
            }

            // force deployment

            // Doorbuster pricing - show regular price without discount
            if (isDoorbuster) {
                const regularPrice = convertNostoPrice(productPrice) || productPrice
                setPrice({
                    price: convertToCurrency(regularPrice),
                    compareAtPrice: convertToCurrency(productListPrice || productPrice),
                    discountPrice: null,
                });
                setDiscountPercent(0);
                return;
            }

            // Hearts collection pricing - show regular price without discount
            if (isHeartsCollection) {
                const regularPrice = convertNostoPrice(productPrice) || productPrice
                setPrice({
                    price: convertToCurrency(regularPrice),
                    compareAtPrice: convertToCurrency(productListPrice || productPrice),
                    discountPrice: null,
                });
                setDiscountPercent(0);
                return;
            }

            // 30% off products
            if (is30Off) {
                const regularPrice = convertNostoPrice(productPrice) || productPrice
                setPrice({
                    price: convertToCurrency(regularPrice),
                    compareAtPrice: convertToCurrency(productListPrice || productPrice),
                    discountPrice: convertToCurrencyNoRound(regularPrice * 0.7),
                });
                setDiscountPercent(30);
                return;
            }



            // All other Monica + Andy products - 50% off
            const regularPrice = convertNostoPrice(productPrice) || productPrice;
            const listPrice = convertToCurrency(productListPrice || productPrice);

            setPrice({
                price: convertToCurrency(regularPrice),
                compareAtPrice: listPrice,
                discountPrice: convertToCurrencyNoRound(regularPrice * 0.5),
            });
            setDiscountPercent(50);
        } else if (isUppababySale) {
            const regularPrice = convertNostoPrice(productPrice) || productPrice
            const listPrice = convertNostoPrice(productListPrice) || productListPrice || regularPrice

            setPrice({
                price: convertToCurrency(convertNostoPrice(productPrice) || productPrice),
                compareAtPrice: convertToCurrency(productListPrice || productPrice),
                discountPrice: null,
            });
            const compareAtPrice = convertToCurrency(productListPrice || productPrice);

            const discountedPrice = convertToCurrency(firstVariantWithInventory?.price);

            const amountOff = compareAtPrice - discountedPrice;
            const discount = Math.round((amountOff / compareAtPrice) * 100);

            setDiscountPercent(discount);
        } else if (vendor !== 'Monica + Andy' && !isInvalidProductType) {

            let regularPrice = convertNostoPrice(productPrice) || productPrice
            let listPrice = convertNostoPrice(productListPrice) || productListPrice
            let discountPrice = null

            // Only show compareAtPrice if listPrice is greater than price
            let compareAtPrice = null
            if (listPrice && regularPrice && listPrice > regularPrice) {
                compareAtPrice = convertToCurrency(listPrice)
            }

            setPrice({
                price: convertToCurrency(regularPrice),
                compareAtPrice: compareAtPrice,
                discountPrice: null,
            })
            setDiscountPercent(0)
        } else if (isInvalidProductType) {
            // Handle invalid product types (like classes) - show price without discounts
            const regularPrice = convertNostoPrice(currentProductPrice) || currentProductPrice || 0
            const listPrice = convertNostoPrice(currentListPrice) || currentListPrice || 0



            // Always set price for classes/events, even if it's 0
            setPrice({
                price: regularPrice === 0 ? 0 : convertToCurrency(regularPrice),
                compareAtPrice: listPrice ? convertToCurrency(listPrice) : null,
                discountPrice: null,
            })
            setDiscountPercent(0)
        }
    }, [
        // Use specific product properties instead of the entire product object
        product?.price,
        product?.productPrice,
        product?.listPrice,
        product?.productListPrice,
        product?.name,
        product?.productName,
        product?.tags1,
        product?.productTags,
        product?.brand,
        product?.productBrand,
        product?.productType,
        product?.customFields,
        product?.skus,
        // Use specific variant properties instead of the entire variant object
        firstVariantWithInventory?.id,
        firstVariantWithInventory?.price,
        firstVariantWithInventory?.listPrice,
        firstVariantWithInventory?.inventoryLevel,
        // Use array length and specific properties instead of entire array
        matchingVariants?.length,
        matchingVariants?.[0]?.id,
        matchingVariants?.[0]?.price,
        matchingVariants?.[0]?.listPrice,
        matchingVariants?.[0]?.inventoryLevel,
        // Primitive values
        variantIdOnLoad,
        brand,
        is30Off,
        is50Off,
        isDoorbuster,
        is40OffDresses,
        isHeartsCollection,
        isUppababySale,
        isInvalidProductType,
        isDisney,
    ])

    return (
        <div
            className={`price-wrap flex ${
                priceType === 'product'
                    ? (placement === 'productpage-complete-the-look' ? 'justify-start' : 'ml-auto')
                    : ''
            }`}
        >
            {price.price !== null && price.price !== undefined ? (
                <div className="price-compare-at-price flex">
                    <div
                        className={`product-price flex items-center gap-1 ${priceType && priceType === 'product'
                                ? 'justify-end'
                                : ''
                            } flex-nowrap ${priceType === 'collectionTile'
                                ? 'lg:text-web-s2-section-title-2 text-mobile-b1-body-1 font-akkurat text-text-primary'
                                : priceType === 'quickBuy'
                                    ? 'text-2xl'
                                    : 'text-2xl '
                            }`}
                    >
                        {price.price &&
                            price.discountPrice &&
                            price.discountPrice !== null &&
                            !price.compareAtPrice ? (
                            <Fragment>
                                <span className={`discounted-price font-akkurat text-text-primary ${
                                    priceType === 'product'
                                        ? 'text-[24px] font-bold'
                                        : 'lg:text-web-s2-section-title-2 text-mobile-b1-body-1'
                                }`}>
                                    {isGiftCard ? 'from ' : ''}${convertToCurrency(price.discountPrice)}
                                </span>
                                <span
                                    className={`compare-at-price font-akkurat text-text-secondary line-through discount-compare this-one ${
                                        priceType === 'product'
                                            ? 'text-[24px]'
                                            : 'text-mobile-b2-body-2 md:text-web-s3-section-content'
                                    }`}
                                >
                                    ${convertToCurrency(price.price)}
                                </span>
                            </Fragment>
                        ) : null}

                        {price.price &&
                            price.discountPrice &&
                            price.discountPrice !== null &&
                            price.compareAtPrice ? (
                            <Fragment>
                                <span className={`discounted-price font-akkurat text-text-primary ${
                                    priceType === 'product'
                                        ? 'text-[24px] font-bold'
                                        : 'md:text-web-s2-section-title-2 text-mobile-b1-body-1'
                                }`}>
                                    {isGiftCard ? 'from ' : ''}${convertToCurrency(price.discountPrice)}
                                </span>
                                <span
                                    className={`compare-at-price text-text-secondary line-through discount-compare ${
                                        priceType === 'product'
                                            ? 'text-[24px]'
                                            : 'text-mobile-b2-body-2 md:text-web-s2-section-title-2'
                                    }`}
                                >
                                    ${convertToCurrency(price.compareAtPrice)}
                                </span>
                            </Fragment>
                        ) : null}

                        {!price.discountPrice &&
                            price &&
                            price.price !== 0 &&
                            price.price !== '0' ? (
                            <span className={`price this-price font-akkurat text-text-primary ${
                                priceType === 'product'
                                    ? (placement === 'productpage-complete-the-look'
                                        ? 'text-mobile-b1-body-1'
                                        : 'text-[24px] font-bold')
                                    : 'lg:text-web-s2-section-title-2 text-mobile-b1-body-1'
                            }`}>
                                {isGiftCard ? 'from ' : ''}${convertToCurrency(price?.price)}
                            </span>
                        ) : null}
                        {!price.discountPrice &&
                            price.compareAtPrice &&
                            price.compareAtPrice !== null &&
                            price.compareAtPrice !== price.price ? (
                            <span className={`ml-1 compare-at-price text-text-danger line-through ${
                                priceType === 'product'
                                    ? 'text-[24px]'
                                    : 'text-mobile-b2-body-2 lg:text-web-s3-section-content'
                            }`}>
                                ${convertToCurrency(price.compareAtPrice)}
                            </span>
                        ) : null}
                        {price.price &&
                            !price.discountPrice &&
                            price.compareAtPrice &&
                            price.price !== price.compareAtPrice &&
                            price.discountPrice > 0 &&
                            price.discountPrice < price.compareAtPrice ? (
                            <Fragment>
                                <span className={`price font-akkurat text-text-primary ${
                                    priceType === 'product'
                                        ? 'text-[24px] font-bold'
                                        : 'lg:text-web-s2-section-title-2 text-mobile-b1-body-1'
                                }`}>
                                    {isGiftCard ? 'from ' : ''}${convertToCurrency(price.discountPrice)}
                                </span>
                                <span className={`ml-1 compare-at-price font-akkurat text-text-secondary line-through ${
                                    priceType === 'product'
                                        ? 'text-[24px]'
                                        : 'text-mobile-b1-body-1 md:text-web-s3-section-content'
                                }`}>
                                    ${convertToCurrency(price.compareAtPrice)}
                                </span>
                            </Fragment>
                        ) : null}

                        {(!price.price === '0' || !price.price === 0) && (
                            <span className="lg:text-web-s2-section-title-2 text-mobile-b1-body-1 font-akkurat text-text-primary">Free</span>
                        )}

                        {discountPercent && discountPercent > 0 ? (
                            <span className={`inline-flex discount-percent text-text-danger font-semibold ${
                                priceType === 'product'
                                    ? 'text-[18px]'
                                    : 'text-mobile-b3-body-3 lg:text-web-s3-section-content'
                            }`}>
                                {discountPercent}% Off
                            </span>
                        ) : null}


                    </div>
                </div>
            ) : (
                <span className="price invalid-price lg:text-web-s2-section-title-2 text-mobile-b1-body-1 font-akkurat text-text-primary">
                    {(product?.price_min || product?.productPriceMin) &&
                        (product?.price_max || product?.productPriceMax) &&
                        (product?.price_min || product?.productPriceMin) !== (product?.price_max || product?.productPriceMax) ? (
                        <span>
                            ${(product?.price_min || product?.productPriceMin)
                                ? convertToCurrency(product?.price_min || product?.productPriceMin)
                                : null}{' '}
                            –{' '}
                            {(product?.price_max || product?.productPriceMax)
                                ? convertToCurrency(product?.price_max || product?.productPriceMax)
                                : null}
                        </span>
                    ) : (
                        <span>
                            ${(product?.price_max || product?.productPriceMax)
                                ? convertToCurrency(product?.price_max || product?.productPriceMax)
                                : null}
                        </span>
                    )}
                </span>
            )}
        </div>
    )
}

export default PriceNosto
